import { useRef } from "react";
import { Link } from "react-router-dom";
import { usePlayer } from "../contexts/PlayerContext";
import { Cover } from "./Cover";
const time = (value: number) =>
  `${Math.floor(value / 60)}:${String(Math.floor(value % 60)).padStart(2, "0")}`;
export function PlayerBar() {
  const p = usePlayer();
  const dialog = useRef<HTMLDialogElement>(null);
  if (!p.current) return null;
  const transport = (
    <div className="player-bar__transport">
      <button onClick={() => p.skip(-15)} aria-label="Back 15 seconds">
        ↶15
      </button>
      <button
        className="player-bar__play"
        onClick={p.toggle}
        aria-label={p.playing ? "Pause" : "Play"}
      >
        {p.playing ? "Ⅱ" : "▶"}
      </button>
      <button onClick={() => p.skip(15)} aria-label="Forward 15 seconds">
        15↷
      </button>
    </div>
  );
  return (
    <>
      <aside className="player-bar" aria-label="Persistent player">
        <div className="player-bar__work">
          <Cover work={p.current} size="mini" />
          <div>
            <div className="player-bar__label">
              {p.buffering ? "BUFFERING…" : "NOW PLAYING"}
            </div>
            <Link to={`/works/${p.current.slug}`}>{p.current.title}</Link>
            <span>
              {p.current.contributors[0]?.name || "Eternally Blessed"}
            </span>
          </div>
        </div>
        {transport}
        <div className="player-bar__timeline">
          <span>{time(p.currentTime)}</span>
          <input
            aria-label="Playback position"
            type="range"
            min="0"
            max={p.duration || 1}
            step="1"
            value={Math.min(p.currentTime, p.duration || 1)}
            onChange={(e) => p.seek(Number(e.target.value))}
          />
          <span>{time(p.duration)}</span>
        </div>
        <button
          className="expand-player"
          onClick={() => dialog.current?.showModal()}
          aria-label="Open player and queue"
        >
          ⌃
        </button>
        {p.error ? (
          <p className="playback-error" role="alert">
            {p.error}
          </p>
        ) : null}
      </aside>
      <dialog ref={dialog} className="focused-player">
        <header>
          <span className="section-kicker">A MOMENT IN THE WORD</span>
          <button
            onClick={() => dialog.current?.close()}
            aria-label="Close player"
          >
            Close
          </button>
        </header>
        <Cover work={p.current} size="hero" />
        <h2>{p.current.title}</h2>
        <p>{p.current.contributors.map((c) => c.name).join(", ")}</p>
        <input
          aria-label="Seek in recording"
          type="range"
          min="0"
          max={p.duration || 1}
          value={Math.min(p.currentTime, p.duration || 1)}
          onChange={(e) => p.seek(Number(e.target.value))}
        />
        <div className="player-times">
          <span>{time(p.currentTime)}</span>
          <span>{time(p.duration)}</span>
        </div>
        {transport}
        {p.error ? <p role="alert">{p.error}</p> : null}
        <div className="player-settings">
          <label>
            Playback speed
            <select
              aria-label="Playback speed"
              value={p.rate}
              onChange={(e) => p.setRate(Number(e.target.value))}
            >
              {[0.75, 1, 1.25, 1.5, 1.75, 2].map((n) => (
                <option value={n} key={n}>
                  {n}×
                </option>
              ))}
            </select>
          </label>
          <label>
            Sleep timer
            <select
              aria-label="Sleep timer"
              value={p.sleepAt ? "active" : "off"}
              onChange={(e) =>
                p.setSleepTimer(
                  e.target.value === "off" ? null : Number(e.target.value),
                )
              }
            >
              <option value="off">Off</option>
              {p.sleepAt ? <option value="active">Timer running</option> : null}
              {[15, 30, 45, 60].map((n) => (
                <option value={n} key={n}>
                  {n} minutes
                </option>
              ))}
            </select>
          </label>
        </div>
        <section className="queue-list">
          <h3>Up next</h3>
          {p.queue.map((w, i) => (
            <div key={w.id}>
              <button
                aria-current={w.id === p.current?.id ? "true" : undefined}
                onClick={() => p.load(w, p.queue)}
              >
                {i + 1}. {w.title}
              </button>
              <button
                aria-label={`Move ${w.title} earlier`}
                disabled={i === 0}
                onClick={() => {
                  const q = [...p.queue];
                  [q[i - 1], q[i]] = [q[i], q[i - 1]];
                  p.setQueue(q);
                }}
              >
                ↑
              </button>
              <button
                aria-label={`Remove ${w.title} from queue`}
                onClick={() => p.setQueue(p.queue.filter((x) => x.id !== w.id))}
              >
                ×
              </button>
            </div>
          ))}
        </section>
      </dialog>
    </>
  );
}
